import React, { useState, useEffect } from 'react'
import { useDebounce, isNilOrEmpty } from 'packages/core'
import { Box, Paper, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import ThresholdSearchInput from '../SearchInput/ThresholdSearchInput'
import PropTypes from 'prop-types'

/**
 * TextSearchFilter - A reusable search component with various width options
 *
 * Features:
 * - Built-in debouncing (configurable, default: 500ms)
 * - Minimum character search threshold (configurable, default: 3 characters)
 * - Clear button for easy reset
 * - Customizable width variants
 * - Customizable search info message
 *
 * Usage:
 * ```jsx
 * // Basic usage with defaults
 * <TextSearchFilter onChange={handleSearch} />
 *
 * // Custom usage with explicit props
 * <TextSearchFilter
 *   variant="widthFull"
 *   placeholder="Find users..."
 *   minCharLength={2}
 *   debounceTime={300}
 *   hideSearchInfoMessage={true}
 *   searchInfoMessage="Custom_Message_Key"
 *   onChange={(value) => handleSearch(value)}
 * />
 * ```
 */

// Styled paper component with outlined variant support
const SearchFilterPaper = styled(Paper)(({ theme, outlined }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#ffffff !important',
    boxShadow: outlined
        ? 'none !important'
        : '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
    border: outlined ? 'solid 0.1rem #d8d8dd' : 'solid 0.1rem #ebebed',
    borderRadius: outlined ? '0.3rem' : '0.5rem',
    padding: outlined ? '0' : '0.2rem 0.4rem',
    height: outlined ? 'initial' : '4rem',
    fontSize: outlined ? '1.3rem' : '1.5rem',
    transition: 'border 0.15s ease-in-out',
    width: '100%',

    '& div:nth-child(1)': {
        border: 'none !important',
        height: 'auto !important',
    },
    '&& div:nth-child(1)': {
        boxShadow: 'none !important',
    },
    '&:hover': {
        border: `0.1rem solid ${theme?.palette?.primary?.main} !important`,
    },
    '&:focus-within': {
        border: `0.1rem solid ${theme?.palette?.primary?.main} !important`,
        boxShadow: outlined
            ? '0 0.4rem 0.8rem 0 rgba(48, 127, 193, 0.33)'
            : 'initial',
    },
}))

// Search icon styling
const SearchIcon = styled(Icon)({
    width: '2rem',
    height: '2rem',
    marginLeft: '0.8rem',
    color: '#aab0b4',
})

// Clear button styling
const ClearIcon = styled(Icon)({
    margin: '0 1rem !important',
    color: '#aab0b4',
    '&:hover': {
        cursor: 'pointer',
    },
})

/**
 * Wrapper component with configurable width
 * - widthSm: 24rem width (default)
 * - widthFull: 100% width
 */
const SearchFilterWrapper = styled(Box)(({ variant }) => {
    const variantStyles = {
        widthSm: {
            width: '24rem',
        },
        widthFull: {
            width: '100%',
        },
    }

    return variantStyles[variant] || variantStyles.widthSm
})

/**
 * TextSearchFilter component for search functionality with width variants
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Size variant: 'widthFull' (100%) or 'widthSm' (24rem, default)
 * @param {boolean} props.outlined - Whether to use outlined styling (default: false)
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {function} props.onChange - Callback when search value changes
 *                                   Only triggers with empty string or min characters reached
 * @param {string} props.value - Optional controlled value
 * @param {Object} props.sx - MUI sx props for the wrapper Box
 * @param {number} props.minCharLength - Minimum characters required for search (default: 3)
 * @param {number} props.debounceTime - Debounce time in milliseconds (default: 500)
 * @param {boolean} props.hideSearchInfoMessage - Whether to hide the search info message (default: false)
 * @param {string} props.searchInfoMessage - Custom translation key for the search info message (default: 'Min_Search_Char_Limit')
 * @returns {React.Component} Text search filter component
 */
const TextSearchFilter = ({
    variant = 'widthSm',
    outlined = false,
    placeholder = '',
    onChange,
    value: controlledValue,
    sx = {},
    minCharLength = 3,
    debounceTime = 500,
    hideSearchInfoMessage = false,
    searchInfoMessage = 'Min_Search_Char_Limit',
    ...rest
}) => {
    const { t } = useTranslation()

    // Initialize state from controlled value
    const [searchKey, setSearchKey] = useState(controlledValue || '')

    // Debounce search value to prevent too many updates
    const debouncedSearchValue = useDebounce(searchKey, debounceTime)

    // Sync with controlled value if provided
    useEffect(() => {
        if (controlledValue !== undefined && controlledValue !== searchKey) {
            setSearchKey(controlledValue)
        }
    }, [controlledValue])

    // Update when debounced value changes
    useEffect(() => {
        // Only trigger updates when:
        // 1. Search is empty (cleared) OR
        // 2. Search has at least minCharLength characters
        if (
            onChange &&
            (debouncedSearchValue?.length === 0 ||
                debouncedSearchValue?.length >= minCharLength)
        ) {
            onChange(debouncedSearchValue)
        }
    }, [debouncedSearchValue, minCharLength])

    // Handle immediate input changes
    const handleChange = (value) => {
        setSearchKey(value)
    }

    // Handle clear button click
    const handleClear = () => {
        setSearchKey('')
    }

    return (
        <SearchFilterWrapper variant={variant} sx={sx}>
            <SearchFilterPaper outlined={outlined}>
                <SearchIcon name="Search" color="#D8D8DD" />

                <Box position="relative" sx={{ flex: 1 }}>
                    <ThresholdSearchInput
                        value={searchKey}
                        handleSearch={handleChange}
                        placeholder={placeholder || t('Search')}
                        minCharacterLength={minCharLength}
                        hideSearchInfoMessage={hideSearchInfoMessage}
                        searchInfoMessage={searchInfoMessage}
                        inputProps={{
                            'aria-label': placeholder || 'Search',
                        }}
                        sx={{
                            borderColor: '#000000',
                            '& > input': {
                                width: '100%',
                                height: '10px',
                                border: 'none',
                                boxShadow: 'none !important',
                            },
                        }}
                        sxNotify={{ position: 'absolute', top: '-33px' }}
                        {...rest}
                    />
                </Box>
                {!isNilOrEmpty(searchKey) && (
                    <ClearIcon
                        name="Close"
                        color="#D8D8DD"
                        role="button"
                        tabIndex={0}
                        aria-label="Clear"
                        onClick={handleClear}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleClear()
                            }
                        }}
                    />
                )}
            </SearchFilterPaper>
        </SearchFilterWrapper>
    )
}

TextSearchFilter.propTypes = {
    variant: PropTypes.oneOf(['widthFull', 'widthSm']),
    outlined: PropTypes.bool,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string,
    sx: PropTypes.object,
    minCharLength: PropTypes.number,
    debounceTime: PropTypes.number,
    hideSearchInfoMessage: PropTypes.bool,
    searchInfoMessage: PropTypes.string,
}

// Make sure React knows this is a valid React component for TypeScript
TextSearchFilter.displayName = 'TextSearchFilter'

export default TextSearchFilter
